import { Site } from "@inrange/building-manager-api-client/models-site";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Container } from "react-bootstrap";

export type ReviewStatus = "pending" | "reviewed" | "skipped";

export interface BulkReviewItemHelpers {
  site: Site;
  index: number;
  total: number;
  /**
   * Mark the current site as reviewed and advance to the next pending site.
   * When there are no more pending sites, the panel stays open on the
   * current site so the operator can review their work or close out.
   */
  saveAndNext: () => void;
  /**
   * Leave the current site untouched and advance to the next pending site.
   */
  skip: () => void;
  /**
   * Close the bulk review panel entirely.
   */
  close: () => void;
}

interface BulkReviewPanelProps {
  title: string;
  sites: Site[];
  onClose: () => void;
  /**
   * Render the per-site review surface. The component owns its own
   * internal state (form values, dirty tracking, in-flight mutations)
   * and calls the supplied helpers when ready to advance or close.
   */
  renderItem: (helpers: BulkReviewItemHelpers) => ReactNode;
  /**
   * Optional per-row secondary slot, rendered under the site name in
   * the left list. When supplied, replaces the default address line so
   * a caller can show its own progress chips (boundary accepted, PV
   * quality, recomputing, ...). Receives the snapshotted Site.
   */
  renderRowSecondary?: (site: Site) => ReactNode;
  /**
   * Optional content rendered above the left list. Mode-specific
   * filter chips / search / counts live here.
   */
  renderListHeader?: () => ReactNode;
}

/**
 * Reusable framework for "bulk review" actions: a full-viewport overlay
 * with a left-hand list of selected sites and a right-hand focused
 * review surface. Each per-site action plug-in provides its own
 * `renderItem` to populate the right panel.
 */
const BulkReviewPanel = ({
  title,
  sites,
  onClose,
  renderItem,
  renderRowSecondary,
  renderListHeader,
}: BulkReviewPanelProps) => {
  const [statusById, setStatusById] = useState<Record<string, ReviewStatus>>(
    () =>
      sites.reduce<Record<string, ReviewStatus>>((acc, site) => {
        acc[site.id] = "pending";
        return acc;
      }, {})
  );
  // Track which site is selected by id, not by position. Filtering
  // can reorder the list or remove the previously-current site
  // entirely; keying on id means we either follow the same site
  // through filter changes, or fall back to the first visible row
  // only when the operator's site genuinely disappears - never
  // silently land on a different site at the same index.
  const [currentSiteId, setCurrentSiteId] = useState<string | undefined>(
    () => sites[0]?.id
  );
  const listScrollRef = useRef<HTMLDivElement | null>(null);

  const currentIndex = useMemo(
    () =>
      currentSiteId ? sites.findIndex((s) => s.id === currentSiteId) : -1,
    [sites, currentSiteId]
  );

  // Fallback to the first row only when the previously-selected
  // site is no longer in the filtered list (operator changed the
  // filter chip in a way that excluded it).
  useEffect(() => {
    if (sites.length === 0) return;
    if (currentSiteId && currentIndex < 0) {
      setCurrentSiteId(sites[0].id);
    } else if (!currentSiteId) {
      setCurrentSiteId(sites[0].id);
    }
  }, [sites, currentSiteId, currentIndex]);

  const selectByIndex = (ii: number) => {
    const target = sites[ii];
    if (target) setCurrentSiteId(target.id);
  };

  // Arrow keys scroll the left list pane without changing
  // selection. Ignored while focus is inside an input / textarea /
  // contenteditable so typing site names still works. Per-site
  // shortcuts (F, J, 1/2/3, B, Y, T, Cmd+.) live on the per-site
  // item; arrow keys belong to the framework.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      const tag = tgt?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        tgt?.isContentEditable
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = listScrollRef.current;
      if (!el) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        el.scrollBy({ top: 60, behavior: "smooth" });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        el.scrollBy({ top: -60, behavior: "smooth" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const currentSite =
    currentIndex >= 0 ? sites[currentIndex] : undefined;
  const reviewedCount = useMemo(
    () => Object.values(statusById).filter((s) => s === "reviewed").length,
    [statusById]
  );
  const skippedCount = useMemo(
    () => Object.values(statusById).filter((s) => s === "skipped").length,
    [statusById]
  );

  const advanceToNextPending = () => {
    if (currentIndex < 0 || sites.length === 0) return;
    for (let offset = 1; offset <= sites.length; offset++) {
      const candidateIndex = (currentIndex + offset) % sites.length;
      const candidate = sites[candidateIndex];
      if (statusById[candidate.id] === "pending") {
        setCurrentSiteId(candidate.id);
        return;
      }
    }
    // No pending sites remain - leave the selection on the current site.
  };

  const setCurrentStatus = (status: ReviewStatus) => {
    if (!currentSite) return;
    setStatusById((prev) => ({ ...prev, [currentSite.id]: status }));
  };

  const helpers: BulkReviewItemHelpers | null = currentSite
    ? {
        site: currentSite,
        index: currentIndex,
        total: sites.length,
        saveAndNext: () => {
          setCurrentStatus("reviewed");
          advanceToNextPending();
        },
        skip: () => {
          setCurrentStatus("skipped");
          advanceToNextPending();
        },
        close: onClose,
      }
    : null;

  return (
    <Container
      fluid
      style={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        // Opt into the Template outlet's `flex: 1; min-height: 0` chain
        // (see comment in routes/Template.tsx) the same way
        // /leads/search does. This way the split panes can scroll
        // independently and the layout is robust to the non-prod
        // warning banner adding a variable amount of header height.
        flex: 1,
        minHeight: 0,
      }}
      data-testid="bulk-review-panel"
    >
      <div
        style={{
          padding: "8px 16px",
          borderBottom: "1px solid #ddd",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <h5 style={{ margin: 0 }}>{title}</h5>
        <Badge bg="secondary">
          {reviewedCount} reviewed
          {skippedCount > 0 ? `, ${skippedCount} skipped` : ""} /{" "}
          {sites.length}
        </Badge>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onClose}
          style={{ marginLeft: "auto" }}
          data-testid="bulk-review-close"
        >
          Exit bulk review
        </Button>
      </div>
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div
          style={{
            width: "30%",
            borderRight: "1px solid #ddd",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {renderListHeader && (
            <div
              style={{
                padding: "8px 12px",
                borderBottom: "1px solid #eee",
                backgroundColor: "#fafbfc",
              }}
              data-testid="bulk-review-list-header"
            >
              {renderListHeader()}
            </div>
          )}
          <div ref={listScrollRef} style={{ flex: 1, overflowY: "auto" }}>
            <SiteList
              sites={sites}
              statusById={statusById}
              currentIndex={currentIndex}
              onSelect={selectByIndex}
              renderRowSecondary={renderRowSecondary}
            />
          </div>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            minWidth: 0,
          }}
        >
          {helpers ? renderItem(helpers) : <EmptyState onClose={onClose} />}
        </div>
      </div>
    </Container>
  );
};

const SiteList = ({
  sites,
  statusById,
  currentIndex,
  onSelect,
  renderRowSecondary,
}: {
  sites: Site[];
  statusById: Record<string, ReviewStatus>;
  currentIndex: number;
  onSelect: (index: number) => void;
  renderRowSecondary?: (site: Site) => ReactNode;
}) => {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {sites.map((site, ii) => {
        const status = statusById[site.id] ?? "pending";
        const isCurrent = ii === currentIndex;
        return (
          <li
            key={site.id}
            onClick={() => onSelect(ii)}
            title={site.address || undefined}
            style={{
              padding: "8px 12px",
              borderBottom: "1px solid #eee",
              backgroundColor: isCurrent ? "#e7f1ff" : undefined,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
            data-testid={`bulk-review-site-${site.id}`}
            data-status={status}
          >
            <span
              style={{
                fontSize: 11,
                color: "#6c757d",
                minWidth: 22,
                textAlign: "right",
                flexShrink: 0,
                fontVariantNumeric: "tabular-nums",
              }}
              data-testid={`bulk-review-row-index-${ii + 1}`}
            >
              {ii + 1}.
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: isCurrent ? 600 : 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {site.name || site.id}
              </div>
              {renderRowSecondary ? (
                <div style={{ marginTop: 4 }}>{renderRowSecondary(site)}</div>
              ) : (
                <div
                  style={{
                    fontSize: 12,
                    color: "#666",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {site.address || "(no address)"}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const EmptyState = ({ onClose }: { onClose: () => void }) => (
  <div style={{ padding: 24 }}>
    <p>No sites to review.</p>
    <Button variant="outline-secondary" onClick={onClose}>
      Close
    </Button>
  </div>
);

export default BulkReviewPanel;
